import {isAnyOf} from '@reduxjs/toolkit'
import {TRACK_CIO_PAGE_VIEW} from 'modules/analytics/actions/trackPageView'
import {setRedirectContext} from 'modules/bookingCreation/slice'
import {createPayrailsSession} from 'modules/paymentSettings/thunks'
import {dissocPath, pipe} from 'ramda'
import {AnyAction} from 'redux'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  identifyAuthenticatedUser,
  userSuccessfullyAuthenticated
} from '@vio/core/src/auth/modules/actions'
import {
  setCustomAppData,
  setUser,
  setWhiteLabelUser
} from '@vio/core/src/auth/modules/slice'
import {updateUserAttributes} from '@vio/core/src/auth/modules/thunks'
import {bookApi} from '@vio/core/src/booking/services/bookApi'
import {bookingRetrieveApi} from '@vio/core/src/booking/services/bookingRetrieveApi'
import {otpApi} from '@vio/core/src/booking/services/otpApi'
import {paymentSettingsApi} from '@vio/core/src/offer/services/paymentSettingsApi'
import {
  setApiContext,
  setGuestDetails
} from '@vio/shared/src/dealFreeze/modules/slice'
import {dealFreezeApi} from '@vio/shared/src/dealFreeze/services/dealFreezeApi'
import {priceWatchApi} from '@vio/shared/src/priceWatch/services'
import {notificationPreferencesApi} from '@vio/shared/src/user/notificationPreferences/modules'

// Their request args, responses or cache keys hold guest or account data, e.g.
// the booking customer, billing address, email or Cognito user id.
const personalDataApiPaths = [
  bookApi,
  bookingRetrieveApi,
  otpApi,
  notificationPreferencesApi,
  priceWatchApi,
  dealFreezeApi
].map(({reducerPath}) => `${reducerPath}/`)

const hasPersonalDataPayload = isAnyOf(
  setRedirectContext,
  identifyAuthenticatedUser,
  userSuccessfullyAuthenticated,
  setUser,
  setWhiteLabelUser,
  setCustomAppData,
  updateUserAttributes.pending,
  updateUserAttributes.fulfilled,
  updateUserAttributes.rejected,
  setGuestDetails,
  setApiContext,
  // Not personal data, but their `data` is a browser-scoped Payrails credential.
  paymentSettingsApi.endpoints.createPaymentSession.matchFulfilled,
  createPayrailsSession.fulfilled
)

const isPersonalDataApiAction = ({type}: AnyAction) =>
  personalDataApiPaths.some(path => type.startsWith(path))

const removeTrackEventPersonalData = pipe(
  dissocPath<AnyAction>(['payload', 'payload', 'userId']),
  dissocPath<AnyAction>([
    'payload',
    'analyticsContext',
    'npsContext',
    'openQuestion'
  ])
)

/**
 * Strips personal data from an action before Sentry records it as a breadcrumb.
 * Meant for the `actionTransformer` option of the Sentry Redux enhancer.
 */
export const removePersonalData = (action: AnyAction) => {
  if (typeof action?.type !== 'string') return action

  if (isPersonalDataApiAction(action))
    return {
      type: action.type,
      endpointName: action.meta?.arg?.endpointName,
      requestStatus: action.meta?.requestStatus,
      // The HTTP status of a failed request, kept to debug booking failures.
      ...(action.meta?.requestStatus === 'rejected' && {
        status: action.payload?.status
      })
    }

  if (hasPersonalDataPayload(action) || action.type === TRACK_CIO_PAGE_VIEW)
    return {type: action.type}

  if (trackEvent.match(action)) return removeTrackEventPersonalData(action)

  return action
}
